import { type InteractiveButton, type Telop } from '@/types/interactiveVideo'
import React, { FC, RefObject, useState } from 'react'
import { resolveBranchButtonStyle } from './branchButtonStyle'
import type TelopDisplay from './TelopDisplay'

/**
 * ボタン描画。色の決め方は `resolveBranchButtonStyle` に集約している
 * （ワイド型の分岐オーバーレイと共有するため）。
 */
const CvButton: FC<{
  button: InteractiveButton
  defaultButtonColor?: string | null
  onClick: (e: React.MouseEvent, button: InteractiveButton) => void
}> = ({ button, defaultButtonColor, onClick }) => {
  const [isHover, setIsHover] = useState(false)
  const style = resolveBranchButtonStyle(button, defaultButtonColor, isHover)

  return (
    <button
      className="w-full mx-auto h-[40px] px-6 rounded-full hover:cursor-pointer font-bold text-[15px] flex items-center justify-center gap-2 transition-colors"
      style={style}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={(e) => onClick(e, button)}
    >
      {button.label}
    </button>
  )
}

export interface ButtonOverlayPresentationProps {
  buttons: InteractiveButton[]
  onClick: (button: InteractiveButton) => void
  onBackClick?: () => void
  showBackButton?: boolean
  TelopDisplay: typeof TelopDisplay
  telops?: Telop[]
  currentTime: number
  showAllButtons: boolean
  scrollContainerRef: RefObject<HTMLDivElement | null>
  onExpandClick: () => void
  /** テナントのデフォルトCTAボタン色（個別ボタン色が無い場合のフォールバック） */
  defaultButtonColor?: string | null
  /** アンケートの帯（下端から32px＋帯の高さ）の分だけボタンを上げる */
  reserveSurveyBar?: boolean
}

const ButtonOverlayPresentation: FC<ButtonOverlayPresentationProps> = ({
  buttons,
  onClick,
  onBackClick,
  showBackButton = false,
  TelopDisplay,
  telops,
  currentTime,
  showAllButtons,
  scrollContainerRef,
  onExpandClick,
  defaultButtonColor,
  reserveSurveyBar = false,
}) => {
  // Limit to maximum 4 buttons
  const displayButtons = buttons.slice(0, 4)
  const hasButtons = displayButtons.length > 0

  const handleClick = (e: React.MouseEvent, button: InteractiveButton) => {
    e.stopPropagation()
    onClick(button)
  }

  const handleBackButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onBackClick?.()
  }

  return (
    <div data-testid="button-overlay" className="absolute inset-0 pointer-events-none">
      {/* 常時表示グラデーション - 動画下部を暗くするデザイン要素 */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-b from-transparent via-black/20 to-black/90 pointer-events-none" />

      {/* Back button - positioned at top left */}
      {showBackButton && (
        <button
          data-testid="back-button"
          className="absolute top-4 left-4 pointer-events-auto px-3 py-2 text-white flex items-center gap-1 z-10 cursor-pointer"
          onClick={handleBackButtonClick}
        >
          <svg
            className="w-[14px] h-[14px]"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-[11px] font-bold">戻る</span>
        </button>
      )}

      {/* ボタン展開表示（ボタンがある場合のみ） */}
      {showAllButtons && hasButtons && (
        <div className="absolute bottom-0 left-0 right-0">
          {/* Scrollable buttons */}
          <div
            ref={scrollContainerRef}
            className="relative max-h-[50vh] overflow-y-auto scrollbar-hide flex flex-col pointer-events-auto"
          >
            {/* アンケートの帯（動画下部・シークバーの上）が出ている間は、帯の分だけ下を空けて
                選択肢 → アンケートの帯 の順に重ならず並べる */}
            <div
              className={`flex flex-col gap-1.5 p-4 ${
                reserveSurveyBar ? 'pb-[80px]' : displayButtons.length >= 3 ? 'pb-6' : 'pb-10'
              }`}
            >
              <TelopDisplay telops={telops} currentTime={currentTime} />
              {displayButtons.map((button) => (
                <CvButton
                  key={button.id}
                  button={button}
                  defaultButtonColor={defaultButtonColor}
                  onClick={handleClick}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* リップルボタン表示（スクロールで隠れた時） */}
      {!showAllButtons && (
        <div className="absolute bottom-0 left-0 right-0">
          <div
            className={`relative flex flex-col items-center gap-4 p-4 pointer-events-auto ${reserveSurveyBar ? 'pb-[80px]' : 'pb-6'}`}
          >
            <TelopDisplay telops={telops} currentTime={currentTime} />
            <button
              onClick={onExpandClick}
              className="ripple-button w-[64px] h-[64px] !rounded-full a-button hover:cursor-pointer flex items-center justify-center transition-colors shadow-lg"
              style={{ background: 'var(--video-brand-color, var(--video-accent-color, #2196F3))' }}
            >
              <svg
                className="w-[24px] h-[24px]"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ButtonOverlayPresentation
